/**
 * @file PickerSheet.tsx
 * @brief Generic searchable picker — centered dialog or right-rail sheet.
 *
 * @details
 * List rows mirror mobile VZPicker soft-fill stadium pills (28-07-2026):
 * - No outline border; soft fill only
 * - Selected → brand label text + check-circle (not a filled badge)
 * - When `renderLeftAccessory` is set → leadingFlush (circle is the left end
 *   of the pill), matching mobile `leadingFlush` / 56px LEADING_CIRCLE
 *
 * `presentation` defaults to right-rail Sheet (30-07-2026). Pass
 * `presentation="dialog"` for a centered modal escape hatch.
 *
 * Menu (30-09-2026): pass `anchor` (the trigger button) and the picker opens
 * as a dropdown under it, the "⋯" menu's look, via `SearchableMenuList`.
 * DES: "saaray picker ab is type kay bana do, country employee etc". Every
 * picker with a button to hang from uses it; the rail stays only for pickers
 * with no trigger on screen.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 30-07-2026 — default presentation rail
 * @updated 30-09-2026 — `anchor` opens it as a dropdown menu
 */

import { Circle, CircleCheck, LoaderCircle, Search, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList } from "@/components/ui/SearchableMenuList";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/shared/theme/cn";

export interface PickerSheetItem {
  id: string;
  label: string;
  subtitle?: string;
  searchKeys?: string[];
  /** Menu mode: rows are headed by group, e.g. "Recent" (04-10-2026). */
  group?: string;
}

export interface PickerSheetProps<T extends PickerSheetItem> {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title: string;
  subtitle?: string;
  searchPlaceholder?: string;

  items: T[];
  /** Single-select (default). Ignored when `multiSelect` is true. */
  selectedId?: string | null;
  onSelect: (item: T) => void;

  /**
   * Multi-select mode (28-07-2026) — mirrors mobile VZPicker `multiSelect`.
   * Rows toggle without closing; use Done to dismiss. `onSelect` still fires
   * per tap so the parent can add/remove from its value array.
   */
  multiSelect?: boolean;
  selectedIds?: string[];

  renderLeftAccessory?: (item: T) => ReactNode;
  /**
   * Optional trailing control (e.g. Share toggle). Called with `(item, selected)`.
   * Nested clicks should stopPropagation so they don't toggle row selection.
   */
  renderTrailingAccessory?: (item: T, selected: boolean) => ReactNode;

  searchValue?: string;
  onSearchChange?: (next: string) => void;

  loading?: boolean;

  clearable?: boolean;
  onClear?: () => void;

  emptyText?: string;

  /**
   * Content at the top of the list, under the search (30-09-2026: Boardroom
   * setup's "add external participant" block, which reads the search text).
   * While it renders something, the "no results" line is not shown.
   */
  listHeader?: ReactNode;

  /**
   * `rail` — right Sheet slide-over (default, all pickers).
   * `dialog` — centered modal (escape hatch).
   */
  presentation?: "dialog" | "rail";

  /**
   * Dialog enter/exit only (ignored for `rail`). Default `zoom`.
   */
  motion?: "zoom" | "fade";

  /**
   * The trigger button. When set, the picker is a dropdown menu under it
   * (the default for every picker with a button), not a rail or dialog. The
   * button must not open the picker itself: the menu toggles it.
   */
  anchor?: ReactElement;
  /** Menu alignment under the anchor. Default `start`. */
  menuAlign?: "start" | "center" | "end";
  /** Menu mode only: a control under the title, shown even when the list is
   *  empty (DES 12-10-2026: "Write new agenda"). */
  menuAction?: ReactNode;
}

export function PickerSheet<T extends PickerSheetItem>({
  open,
  onOpenChange,
  title,
  subtitle,
  searchPlaceholder = "Search…",
  items,
  selectedId,
  onSelect,
  multiSelect = false,
  selectedIds,
  renderLeftAccessory,
  renderTrailingAccessory,
  searchValue,
  onSearchChange,
  loading,
  clearable = true,
  onClear,
  emptyText,
  listHeader,
  presentation = "rail",
  motion = "zoom",
  anchor,
  menuAlign = "start",
  menuAction,
}: PickerSheetProps<T>) {
  const isControlled = onSearchChange !== undefined;
  const [internalSearch, setInternalSearch] = useState("");
  const search = isControlled ? (searchValue ?? "") : internalSearch;

  // Avatar / flag / gender glyph rows: leading circle is the pill's left end.
  const leadingFlush = !!renderLeftAccessory;

  useEffect(() => {
    if (!open && !isControlled) setInternalSearch("");
  }, [open, isControlled]);

  const selectedSet = useMemo(() => {
    if (multiSelect) return new Set(selectedIds ?? []);
    return new Set(selectedId ? [selectedId] : []);
  }, [multiSelect, selectedId, selectedIds]);

  const visibleItems: T[] = useMemo(() => {
    if (isControlled) return items;
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const haystack: string[] = [item.label, item.subtitle ?? "", ...(item.searchKeys ?? [])];
      return haystack.some((s) => s.toLowerCase().includes(q));
    });
  }, [isControlled, items, search]);

  const selectedCount = selectedSet.size;

  if (anchor) {
    const byId = new Map(items.map((it) => [it.id, it]));
    return (
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>{anchor}</DropdownMenuTrigger>
        <DropdownMenuContent
          align={menuAlign}
          className="w-[max(var(--radix-dropdown-menu-trigger-width),18rem)] max-w-[calc(100vw-2rem)] p-1.5"
        >
          <SearchableMenuList
            title={title}
            searchPlaceholder={searchPlaceholder}
            items={visibleItems.map((it) => ({
              value: it.id,
              label: it.label,
              description: it.subtitle,
              group: it.group,
              icon: renderLeftAccessory ? (
                // Rail accessories are 56px; the menu's are 28px.
                <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full text-[10px] [&>*]:!h-full [&>*]:!min-h-0 [&>*]:!w-full [&>*]:!min-w-0 [&>*]:!text-[10px] [&_svg]:!h-3.5 [&_svg]:!w-3.5">
                  {renderLeftAccessory(it)}
                </span>
              ) : undefined,
              trailing: renderTrailingAccessory?.(it, selectedSet.has(it.id)) ?? undefined,
            }))}
            selected={[...selectedSet]}
            multiple={multiSelect}
            onSelect={(id) => {
              const it = byId.get(id);
              if (it) onSelect(it);
            }}
            // Filtering already happened above (search keys included).
            query={search}
            onQueryChange={(next) =>
              isControlled ? onSearchChange!(next) : setInternalSearch(next)
            }
            loading={loading}
            emptyText={emptyText ?? "Nothing to choose from yet."}
            noMatchText={emptyText ?? `No results match “${search}”.`}
            header={listHeader}
            action={menuAction}
            footer={
              clearable && selectedCount > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      if (multiSelect) e.preventDefault();
                      onClear?.();
                    }}
                    className="text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                    {multiSelect ? `Clear all (${selectedCount})` : "Clear selection"}
                  </DropdownMenuItem>
                </>
              ) : null
            }
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const body = (
    <>
      <div className="border-b px-6 py-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={search}
            placeholder={searchPlaceholder}
            onChange={(e) =>
              isControlled ? onSearchChange!(e.target.value) : setInternalSearch(e.target.value)
            }
            className="pl-10"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {clearable && selectedCount > 0 ? (
          <button
            type="button"
            onClick={() => {
              onClear?.();
              if (!multiSelect) onOpenChange(false);
            }}
            className="mb-2 flex h-12 w-full items-center gap-3 rounded-full bg-muted px-4 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
          >
            <X className="h-4 w-4" />
            <span>{multiSelect ? `Clear all (${selectedCount})` : "Clear selection"}</span>
          </button>
        ) : null}

        {loading && visibleItems.length === 0 ? (
          <div className="flex items-center gap-2 px-4 py-6 text-xs text-muted-foreground">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : null}

        {listHeader}

        {!loading && visibleItems.length === 0 && !listHeader ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            {emptyText ?? `No results match “${search}”.`}
          </div>
        ) : null}

        <ul role="listbox" aria-multiselectable={multiSelect || undefined} className="space-y-2">
          {visibleItems.map((item) => {
            const isSelected = selectedSet.has(item.id);
            const trailing = renderTrailingAccessory?.(item, isSelected);
            return (
              <li key={item.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onSelect(item);
                    if (!multiSelect) onOpenChange(false);
                  }}
                  className={cn(
                    // Soft-fill stadium — no outline (mobile VZPicker parity).
                    // `p-0` kills UA button padding so leadingFlush can sit at x=0.
                    "flex w-full items-center p-0 text-left transition-colors",
                    "rounded-full bg-muted hover:bg-muted/80",
                    leadingFlush
                      ? "h-14 overflow-hidden"
                      : "min-h-[52px] justify-between gap-3 px-4 py-2.5",
                  )}
                >
                  {leadingFlush ? (
                    <>
                      {/* Leading circle is the left end of the pill (mobile leadingFlush). */}
                      <span className="flex h-14 w-14 shrink-0 items-stretch justify-stretch overflow-hidden [&>*]:h-full [&>*]:max-h-full [&>*]:w-full [&>*]:max-w-full">
                        {renderLeftAccessory?.(item)}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col justify-center gap-0 py-2 pl-3 leading-tight">
                        <span
                          className={cn(
                            "truncate text-[15px] font-semibold leading-5",
                            isSelected ? "text-brand" : "text-foreground",
                          )}
                        >
                          {item.label}
                        </span>
                        {item.subtitle ? (
                          <span className="truncate text-xs leading-[14px] text-muted-foreground">
                            {item.subtitle}
                          </span>
                        ) : null}
                      </span>
                      <span className="flex shrink-0 items-center gap-2 pr-4">
                        {trailing ? (
                          <span
                            className="flex items-center"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {trailing}
                          </span>
                        ) : null}
                        {isSelected ? (
                          <CircleCheck
                            className="h-[22px] w-[22px] shrink-0 text-brand"
                            aria-hidden
                          />
                        ) : (
                          <Circle
                            className="h-[22px] w-[22px] shrink-0 text-muted-foreground/50"
                            aria-hidden
                          />
                        )}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="flex min-w-0 flex-1 items-center gap-4">
                        <span className="flex min-w-0 flex-col leading-tight">
                          <span
                            className={cn(
                              "truncate text-sm font-semibold",
                              isSelected ? "text-brand" : "text-foreground",
                            )}
                          >
                            {item.label}
                          </span>
                          {item.subtitle ? (
                            <span className="truncate text-xs text-muted-foreground">
                              {item.subtitle}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {trailing ? (
                          <span
                            className="flex items-center"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            {trailing}
                          </span>
                        ) : null}
                        {isSelected ? (
                          <CircleCheck
                            className="h-[22px] w-[22px] shrink-0 text-brand"
                            aria-hidden
                          />
                        ) : (
                          <Circle
                            className="h-[22px] w-[22px] shrink-0 text-muted-foreground/50"
                            aria-hidden
                          />
                        )}
                      </span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {multiSelect ? (
        <div className="border-t px-4 py-3">
          <Button
            type="button"
            className="h-11 w-full rounded-full"
            onClick={() => onOpenChange(false)}
          >
            Done
            {selectedCount > 0 ? ` (${selectedCount})` : ""}
          </Button>
        </div>
      ) : null}
    </>
  );

  if (presentation === "rail") {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        {/* ⚠️ Every breakpoint Sheet sets (sm AND lg) must be overridden:
            `sm:max-w-md` alone lost to the base `lg:max-w-2xl`, so pickers
            ran 672px wide on desktop. (30-09-2026) */}
        <SheetContent
          side="right"
          className="flex max-h-dvh w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md lg:max-w-md"
        >
          <SheetHeader className="space-y-1">
            <SheetTitle>{title}</SheetTitle>
            {subtitle ? <SheetDescription>{subtitle}</SheetDescription> : null}
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent motion={motion} className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-md">
        <DialogHeader className="space-y-1 border-b px-6 pb-4 pt-6 text-left">
          <DialogTitle className="text-lg">{title}</DialogTitle>
          {subtitle ? <DialogDescription>{subtitle}</DialogDescription> : null}
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
